import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PackageCheck, ArrowRight, CheckCircle2 } from "lucide-react";
import api from "../../lib/api";
import { Card, Badge, Button, Spinner, EmptyState } from "../../lib/ui";
import { fmtRp, fmtDateWIB } from "../../lib/constants";

function POList({ rows, tone, emptyText, action, navigate }) {
  if (!rows) return <div className="flex h-28 items-center justify-center"><Spinner className="h-6 w-6" /></div>;
  if (rows.length === 0) return <div className="p-5"><EmptyState image={null} title={emptyText} /></div>;
  return (
    <div className="divide-y divide-line">
      {rows.map((po) => (
        <div key={po.id} className="flex items-center gap-3 px-5 py-3" data-testid={`sc-recv-${po.id}`}>
          <PackageCheck size={18} className={tone} />
          <div className="min-w-0 flex-1">
            <p className="truncate font-bold text-ink">{po.supplier_name || "Tanpa Supplier"}</p>
            <p className="text-xs text-ink-mute">{po.po_no} · {fmtDateWIB(po.received_at || po.approved_at || po.created_at)} · {(po.items || []).length} item</p>
          </div>
          <p className="shrink-0 font-heading font-extrabold text-ink">{fmtRp(po.total_cost)}</p>
          {action && (
            <Button size="sm" onClick={() => navigate("/procurement")} data-testid={`sc-recv-action-${po.id}`}>
              {action} <ArrowRight size={14} />
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

export default function ReceivingTab() {
  const navigate = useNavigate();
  const [approved, setApproved] = useState(null);
  const [received, setReceived] = useState(null);

  const load = useCallback(async () => {
    try {
      const [a, r] = await Promise.all([
        api.get("/purchase-orders", { params: { status: "APPROVED" } }),
        api.get("/purchase-orders", { params: { status: "RECEIVED" } }),
      ]);
      setApproved(Array.isArray(a.data) ? a.data : a.data.rows || []);
      setReceived(Array.isArray(r.data) ? r.data : r.data.rows || []);
    } catch { setApproved([]); setReceived([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="sc-receiving">
      <Card className="overflow-hidden p-0">
        <div className="flex items-center gap-2 border-b border-line bg-amber-50 px-5 py-4 text-amber-700">
          <PackageCheck size={18} />
          <h3 className="font-heading text-base font-extrabold">Menunggu Penerimaan</h3>
          <Badge tone="mute" className="ml-auto">{(approved || []).length} PO</Badge>
        </div>
        <POList rows={approved} tone="text-amber-600" emptyText="Tidak ada PO yang menunggu diterima" action="Terima" navigate={navigate} />
      </Card>
      <Card className="overflow-hidden p-0">
        <div className="flex items-center gap-2 border-b border-line bg-emerald-50 px-5 py-4 text-emerald-700">
          <CheckCircle2 size={18} />
          <h3 className="font-heading text-base font-extrabold">Diterima Terbaru</h3>
          <Badge tone="mute" className="ml-auto">{(received || []).length} PO</Badge>
        </div>
        <POList rows={(received || []).slice(0, 30)} tone="text-emerald-600" emptyText="Belum ada barang diterima" navigate={navigate} />
      </Card>
    </div>
  );
}
